import { useEffect, useRef, useState } from 'react'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

const GLYPHS = '#$%&/0123456789@ABCDEFGHIJKLMNOPQRSTUVWXYZ'

interface ScrambleTextProps {
  text: string
  className?: string
  speedMs?: number
}

export function ScrambleText({ text, className = '', speedMs = 28 }: ScrambleTextProps) {
  const reduced = useReducedMotion()
  const [ref, inView] = useInView<HTMLSpanElement>({ threshold: 0.5 })
  const [output, setOutput] = useState(text)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (!inView || reduced) {
      setOutput(text)
      return
    }

    let revealed = 0
    const characters = Array.from(text)

    const run = () => {
      const next = characters
        .map((character, index) => {
          if (index < revealed || character === ' ') return character
          return GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? character
        })
        .join('')

      setOutput(next)
      revealed += 1

      if (revealed <= characters.length) {
        timer.current = window.setTimeout(run, speedMs)
      } else {
        setOutput(text)
      }
    }

    run()

    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
  }, [inView, reduced, text, speedMs])

  return (
    <span ref={ref} className={className} data-text={text}>
      {output}
    </span>
  )
}
